
// src/components/pipsCreatureKeeper/FieldJournal.tsx

import React, { useState } from 'react';
import {
  IonCard,
  IonCardContent,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonBadge,
  IonContent,
  IonProgressBar
} from '@ionic/react';
import { close, checkmarkCircle, helpCircle } from 'ionicons/icons';
import { Creature, FieldJournalEntry, PlayerCreature } from '../../../types/pipsCreatureKeeper';
import { getCreatureCompatibility } from '../../../services/pipsCreatureKeeperHabitatService';
import './FieldJournal.css';

interface FieldJournalProps {
  creatures: Creature[];
  fieldJournal: FieldJournalEntry[];
  myCreatures: PlayerCreature[];
  typeCompatibility: any[];
}

const FieldJournal: React.FC<FieldJournalProps> = ({
  creatures,
  fieldJournal,
  myCreatures,
  typeCompatibility
}) => {
  const [selectedCreature, setSelectedCreature] = useState<Creature | null>(null);

  const discoveredIds = new Set(fieldJournal.map(fj => fj.creature_id));
  const capturedIds = new Set(myCreatures.map(mc => mc.creature_id));

  const discoveredCount = fieldJournal.length;
  const capturedCount = myCreatures.length;
  const totalCreatures = 50;

  const discoveryProgress = (discoveredCount / totalCreatures) * 100;
  const captureProgress = (capturedCount / totalCreatures) * 100;

  const getTypeColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      flame: '#FF4500',
      tide: '#1E90FF',
      stone: '#696969',
      storm: '#9370DB',
      frost: '#87CEEB',
      bloom: '#32CD32',
      shadow: '#2F2F2F',
      light: '#FFD700',
      metal: '#C0C0C0',
      spirit: '#DDA0DD'
    };
    return colors[type.toLowerCase()] || '#999';
  };

  const getTypeTextColor = (type: string): string => {
    const darkTypes = ['stone', 'shadow', 'spirit'];
    return darkTypes.includes(type.toLowerCase()) ? '#fff' : '#000';
  };

  const handleCreatureClick = (creature: Creature) => {
    // Only open modal if creature is discovered
    if (discoveredIds.has(creature.id)) {
      setSelectedCreature(creature);
    }
  };

  const closeModal = () => {
    setSelectedCreature(null);
  };

  const renderCreatureCard = (creature: Creature) => {
    const discovered = discoveredIds.has(creature.id);
    const captured = capturedIds.has(creature.id);

    if (!discovered) {
      // Mystery card
      return (
        <div key={creature.id} className="journal-card mystery">
          <div className="journal-image mystery-image">
            <div className="mystery-icon">❓</div>
          </div>
          <div className="journal-name">???</div>
          <div className="journal-status unknown">Unknown</div>
        </div>
      );
    }

    return (
      <div
        key={creature.id}
        className={`journal-card ${captured ? 'captured' : 'discovered'}`}
        onClick={() => handleCreatureClick(creature)}
      >
        {/* Status badge */}
        <div className={`status-badge ${captured ? 'badge-captured' : 'badge-discovered'}`}>
          <IonIcon icon={captured ? checkmarkCircle : helpCircle} />
        </div>

        {/* Creature image */}
        <div className="journal-image">
          {creature.image_url ? (
            <img src={creature.image_url} alt={creature.name} />
          ) : creature.ascii_art ? (
            <pre className="ascii-art-journal">{creature.ascii_art}</pre>
          ) : (
            <div className="placeholder-journal">🦋</div>
          )}
        </div>

        {/* Creature name */}
        <div className="journal-name">{creature.name}</div>

        {/* Types */}
        <div className="journal-types">
          <IonBadge
            className="type-badge-journal"
            style={{
              '--background': getTypeColor(creature.type1),
              color: getTypeTextColor(creature.type1)
            }}
          >
            {creature.type1}
          </IonBadge>
          {creature.type2 && (
            <IonBadge
              className="type-badge-journal"
              style={{
                '--background': getTypeColor(creature.type2),
                color: getTypeTextColor(creature.type2)
              }}
            >
              {creature.type2}
            </IonBadge>
          )}
        </div>

        {/* Status */}
        <div className={`journal-status ${captured ? 'status-captured' : 'status-discovered'}`}>
          {captured ? 'Captured' : 'Discovered'}
        </div>
      </div>
    );
  };

  return (
    <div className="field-journal-container">
      {/* Progress section */}
      <IonCard className="progress-card">
        <IonCardContent>
          <h2 className="journal-title">Species Discovery Journal</h2>

          <div className="progress-section">
            <div className="progress-item">
              <div className="progress-label">
                <span className="progress-text">Discovered</span>
                <span className="progress-count">{discoveredCount}/{totalCreatures}</span>
              </div>
              <IonProgressBar value={discoveryProgress / 100} color="primary" />
            </div>

            <div className="progress-item">
              <div className="progress-label">
                <span className="progress-text">Captured</span>
                <span className="progress-count">{capturedCount}/{totalCreatures}</span>
              </div>
              <IonProgressBar value={captureProgress / 100} color="success" />
            </div>
          </div>

          <p className="journal-hint">
            Click on discovered creatures to view detailed information. Mystery creatures (❓) will be
            revealed as you explore territories and encounter them in the wild.
          </p>
        </IonCardContent>
      </IonCard>

      {/* Creatures grid */}
      <div className="journal-grid">
        {creatures.map(creature => renderCreatureCard(creature))}
      </div>

      {/* Creature detail modal */}
      <IonModal isOpen={!!selectedCreature} onDidDismiss={closeModal}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{selectedCreature?.name}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={closeModal}>
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="modal-content">
          {selectedCreature && (
            <div className="creature-detail">
              {/* Image */}
              <div className="detail-image">
                {selectedCreature.image_url ? (
                  <img src={selectedCreature.image_url} alt={selectedCreature.name} />
                ) : selectedCreature.ascii_art ? (
                  <pre className="ascii-art-detail">{selectedCreature.ascii_art}</pre>
                ) : (
                  <div className="placeholder-detail">🦋</div>
                )}
              </div>

              {/* Name and types */}
              <h2 className="detail-name">{selectedCreature.name}</h2>
              <div className="detail-types">
                <IonBadge
                  style={{
                    '--background': getTypeColor(selectedCreature.type1),
                    color: getTypeTextColor(selectedCreature.type1),
                    fontSize: '14px',
                    padding: '6px 12px'
                  }}
                >
                  {selectedCreature.type1}
                </IonBadge>
                {selectedCreature.type2 && (
                  <IonBadge
                    style={{
                      '--background': getTypeColor(selectedCreature.type2),
                      color: getTypeTextColor(selectedCreature.type2),
                      fontSize: '14px',
                      padding: '6px 12px'
                    }}
                  >
                    {selectedCreature.type2}
                  </IonBadge>
                )}
              </div>

              {/* Status badge */}
              <div className="detail-status">
                <IonBadge
                  color={capturedIds.has(selectedCreature.id) ? 'success' : 'warning'}
                  style={{ fontSize: '13px', padding: '8px 16px', color: '#000' }}
                >
                  {capturedIds.has(selectedCreature.id) ? '✓ Captured' : 'Discovered'}
                </IonBadge>
              </div>

              {/* Description */}
              {selectedCreature.description && (
                <IonCard>
                  <IonCardContent>
                    <h3 className="section-title">Description</h3>
                    <p className="detail-description">{selectedCreature.description}</p>
                  </IonCardContent>
                </IonCard>
              )}

              {/* Type compatibility */}
              <IonCard>
                <IonCardContent>
                  <h3 className="section-title">Type Compatibility</h3>
                  {(() => {
                    const types = [selectedCreature.type1, selectedCreature.type2].filter(Boolean) as string[];
                    const compatibility = getCreatureCompatibility(types, typeCompatibility);

                    return (
                      <div className="compatibility-info">
                        {compatibility.strong.length > 0 && (
                          <div className="compatibility-section happy">
                            <div className="compatibility-header">
                              <span className="compatibility-icon">😊</span>
                              <span className="compatibility-label">Happy with:</span>
                            </div>
                            <div className="compatibility-tags">
                              {compatibility.strong.map(type => (
                                <IonBadge
                                  key={type}
                                  style={{
                                    '--background': getTypeColor(type),
                                    color: getTypeTextColor(type),
                                    margin: '4px'
                                  }}
                                >
                                  {type}
                                </IonBadge>
                              ))}
                            </div>
                          </div>
                        )}

                        {compatibility.weak.length > 0 && (
                          <div className="compatibility-section unhappy">
                            <div className="compatibility-header">
                              <span className="compatibility-icon">😰</span>
                              <span className="compatibility-label">Uncomfortable with:</span>
                            </div>
                            <div className="compatibility-tags">
                              {compatibility.weak.map(type => (
                                <IonBadge
                                  key={type}
                                  style={{
                                    '--background': getTypeColor(type),
                                    color: getTypeTextColor(type),
                                    margin: '4px'
                                  }}
                                >
                                  {type}
                                </IonBadge>
                              ))}
                            </div>
                          </div>
                        )}

                        {compatibility.strong.length === 0 && compatibility.weak.length === 0 && (
                          <p style={{ margin: 0, color: '#666', fontStyle: 'italic' }}>
                            Neutral compatibility with all types
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </IonCardContent>
              </IonCard>

              {/* Capture tip if not captured */}
              {!capturedIds.has(selectedCreature.id) && (
                <IonCard className="tip-card">
                  <IonCardContent>
                    <h3 className="section-title">💡 Capture Tip</h3>
                    <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: '#856404' }}>
                      To capture this creature, explore territories with a companion whose type is compatible.
                      Check the type compatibility chart above to find the best companion!
                    </p>
                  </IonCardContent>
                </IonCard>
              )}
            </div>
          )}
        </IonContent>
      </IonModal>
    </div>
  );
};

export default FieldJournal;